import React, { useEffect } from 'react';
import { motion } from 'framer-motion';

// ─────────────────────────────────────────────────────────────────────────────
// PrivacyPolicy.js — modern dark mode layout matching Testimonial/About
// ─────────────────────────────────────────────────────────────────────────────

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } },
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15 } },
};

export default function PrivacyPolicy() {
  useEffect(() => {
    document.title = 'Flap Card Privacy Policy';
  }, []);

  return (
    <section className="flap-privacy min-h-screen bg-[#06112b] text-white relative overflow-hidden">
      {/* Global styles — consistent with Testimonial.js */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
        @import url('https://api.fontshare.com/v2/css?f[]=clash-display@400,500,600,700&display=swap');

        .flap-privacy {
          padding: 120px 5% 100px;
          position: relative;
          font-family: 'DM Sans', sans-serif;
        }

        .flap-privacy::before {
          content: '';
          position: absolute; inset: 0;
          background-image:
            linear-gradient(rgba(28,115,186,0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(28,115,186,0.05) 1px, transparent 1px);
          background-size: 52px 52px;
          pointer-events: none;
        }

        .flap-privacy::after {
          content: '';
          position: absolute;
          width: 600px; height: 600px;
          background: radial-gradient(circle, rgba(28,115,186,0.12) 0%, transparent 70%);
          top: -150px; right: -150px;
          pointer-events: none;
        }

        .flap-privacy-inner {
          max-width: 1100px;
          margin: 0 auto;
          position: relative;
          z-index: 1;
        }

        .privacy-label {
          display: block;
          font-size: 0.75rem;
          font-weight: 700;
          letter-spacing: 2.5px;
          text-transform: uppercase;
          color: #f5a623;
          margin-bottom: 16px;
          text-align: center;
        }

        .privacy-heading {
          font-family: 'Clash Display', sans-serif;
          font-size: clamp(2.5rem, 6vw, 4.5rem);
          font-weight: 700;
          text-align: center;
          line-height: 1.15;
          margin-bottom: 60px;
          color: #d03b0d;
        }

        .privacy-heading span {
          color: #1c73ba;
        }

        .privacy-date {
          text-align: center;
          font-size: 1rem;
          color: rgba(255,255,255,0.6);
          margin-bottom: 48px;
        }

        .privacy-content {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 24px;
          padding: 48px 32px;
          backdrop-filter: blur(12px);
          box-shadow: 0 10px 40px rgba(0,0,0,0.3);
        }

        .privacy-section-title {
          font-family: 'Clash Display', sans-serif;
          font-size: 2.2rem;
          font-weight: 600;
          color: #18042e;
          margin: 64px 0 24px;
        }

        .privacy-section-title:first-child {
          margin-top: 0;
        }

        .privacy-text {
          color: rgba(15, 4, 56, 0.85);
          line-height: 1.8;
          font-size: 1.05rem;
          margin-bottom: 24px;
        }

        .privacy-list {
          list-style: disc;
          padding-left: 32px;
          margin-bottom: 32px;
        }

        .privacy-list li {
          margin-bottom: 16px;
          color: rgba(22, 2, 45, 0.9);
          font-size: 1.05rem;
          line-height: 1.7;
        }

        .privacy-link {
          color: #5aaef7;
          text-decoration: underline;
          transition: color 0.2s;
        }

        .privacy-link:hover {
          color: #1c73ba;
        }

        .privacy-subsection-title {
          font-size: 1.4rem;
          font-weight: 600;
          color: #770a0a;
          margin: 32px 0 16px;
        }

        @media (max-width: 768px) {
          .privacy-heading { font-size: clamp(2.2rem, 7vw, 3.8rem); }
          .privacy-content { padding: 32px 24px; }
        }
      `}</style>

      <div className="flap-privacy-inner">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.span className="privacy-label" variants={fadeInUp}>
            Legal & Privacy
          </motion.span>

          <motion.h1 className="privacy-heading" variants={fadeInUp}>
            Privacy <span>Policy</span>
          </motion.h1>

          <motion.p className="privacy-date" variants={fadeInUp}>
            Effective Date: June 01, 2025
          </motion.p>

          <motion.div className="privacy-content" variants={fadeInUp}>
            <p className="privacy-text">
              Flap Card is a digital contact-sharing platform operated by <a href="https://www.flap.com.np" className="privacy-link">Flap Tech Pvt Ltd</a> ("Flap Card," "we," "us," or "our"), a Nepal-based company. This Privacy Policy explains how we collect, use, protect, and share your personally identifiable information when you use our Software Applications or Services. It also describes your rights and choices regarding your data.
            </p>

            <p className="privacy-text">
              By accessing or using our Services, you acknowledge that you have read, understood, and agree to this Policy.
            </p>

            <h2 className="privacy-section-title">Our Privacy Principles</h2>
            <ul className="privacy-list">
              <li><strong>Your Data is Yours:</strong> We do not sell or share your Personal Information without your explicit consent. You control how your data is shared.</li>
              <li><strong>Secure Storage:</strong> Enterprise-grade encryption and security measures protect your data at all times.</li>
              <li><strong>Safe Sharing:</strong> Each Flap has a unique code — you decide who receives it. Be aware that recipients may share it further.</li>
            </ul>

            <h2 className="privacy-section-title">1. Information We Collect</h2>
            <h3 className="privacy-subsection-title">1.1 Information You Provide</h3>
            <ul className="privacy-list">
              <li>Account details (name, email, username, password, country)</li>
              <li>Contact & payment information (when you provide it)</li>
              <li>Geolocation (with consent, for location features)</li>
              <li>Mobile device permissions (contacts, calendar — with your approval)</li>
            </ul>

            <h3 className="privacy-subsection-title">1.2 Automatically Collected Information</h3>
            <ul className="privacy-list">
              <li>Usage data (pages visited, features used)</li>
              <li>Device information (IP, browser, OS, device ID)</li>
              <li>Analytics (anonymized, for performance & security)</li>
            </ul>

            <h2 className="privacy-section-title">2. How We Use Your Information</h2>
            <ul className="privacy-list">
              <li>To provide, maintain, and improve the Services</li>
              <li>To process transactions and personalize your experience</li>
              <li>To communicate with you (support, updates, offers — with consent)</li>
              <li>To detect fraud, enforce terms, and comply with legal obligations</li>
            </ul>

            <h2 className="privacy-section-title">3. How We Share Your Information</h2>
            <ul className="privacy-list">
              <li>With service providers (payment processors, cloud storage — bound by confidentiality)</li>
              <li>When you share your Flap Card (recipient gets your chosen data)</li>
              <li>For legal compliance (court orders, subpoenas)</li>
              <li>In business transfers (merger, acquisition)</li>
            </ul>

            <h2 className="privacy-section-title">4. Data Security</h2>
            <p className="privacy-text">
              We use SSL encryption, secure storage, and industry-standard protections. While we strive for absolute security, no system is 100% immune. You are responsible for keeping your credentials safe.
            </p>

            <h2 className="privacy-section-title">5. Your Rights</h2>
            <ul className="privacy-list">
              <li>Access, correct, or delete your data</li>
              <li>Opt out of marketing communications</li>
              <li>Request data portability</li>
              <li>Contact us at <a href="mailto:privacy@flap.com.np" className="privacy-link">privacy@flap.com.np</a></li>
            </ul>

            <h2 className="privacy-section-title">6. Contact Us</h2>
            <p className="privacy-text">
              Questions? Reach out:<br />
              Email: <a href="mailto:privacy@flap.com.np" className="privacy-link">privacy@flap.com.np</a><br />
              Address: Lazimpat-2, Kathmandu, Nepal
            </p>

            <p className="privacy-text mt-12 text-center text-gray-400 text-sm">
              Thank you for trusting Flap Card with your information.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}